import type { MenuHandler } from '@blocksuite/affine-components/context-menu';
import {
  STREAMLAIN_PANEL_LAYER_ENTER,
  type StreamLainAnimationController,
  streamLainAnimationController,
  type StreamLainAnimationHandle,
} from '@blocksuite/affine-shared/utils';
import { noChange } from 'lit';
import { AsyncDirective } from 'lit/async-directive.js';
import {
  directive,
  type ElementPart,
  type PartInfo,
  PartType,
} from 'lit/directive.js';

import { onClickOutside } from '../../../core/utils/utils.js';

/** Calendar feedback delegates timing, reduced motion and cancellation to the shared controller. */
export class CalendarFeedbackMotion {
  private root: HTMLElement | undefined;
  private releaseOutside: (() => void) | undefined;
  private readonly handles = new Map<HTMLElement, StreamLainAnimationHandle>();
  constructor(
    private readonly controller: StreamLainAnimationController = streamLainAnimationController
  ) {}
  readonly pulse = (target: EventTarget | null): void => {
    if (!(target instanceof Element)) return;
    const element = target.closest<HTMLElement>(
      'button,summary,[role="button"]'
    );
    if (!element || element.matches(':disabled') || element.closest('[inert]'))
      return;
    const handle = this.controller.play(
      element,
      [{ opacity: 0.72 }, { opacity: 1 }],
      'feedback'
    );
    this.handles.set(element, handle);
    void handle.finished.then(() => {
      if (this.handles.get(element) === handle) this.handles.delete(element);
    });
  };
  private menus() {
    return (
      this.root?.querySelectorAll<HTMLDetailsElement>(
        '[data-calendar-more-menu],[data-calendar-display-menu]'
      ) ?? []
    );
  }
  private closeMenus(except?: HTMLDetailsElement) {
    for (const details of this.menus()) {
      if (details === except) continue;
      const summary = details.querySelector<HTMLElement>(':scope > summary');
      if (summary?.getAttribute('aria-expanded') === 'true') summary.click();
    }
  }
  private readonly onClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    this.closeMenus(
      target.closest<HTMLDetailsElement>(
        '[data-calendar-more-menu],[data-calendar-display-menu]'
      ) ?? undefined
    );
  };
  private readonly onFocusOut = (event: FocusEvent) => {
    for (const details of this.menus()) {
      if (
        event.target instanceof Node &&
        details.contains(event.target) &&
        event.relatedTarget instanceof Node &&
        !details.contains(event.relatedTarget)
      ) {
        const summary = details.querySelector<HTMLElement>(':scope > summary');
        if (summary?.getAttribute('aria-expanded') === 'true') summary.click();
      }
    }
  };
  private readonly onMenuKey = (event: KeyboardEvent) => {
    if (
      event.defaultPrevented ||
      event.isComposing ||
      !(event.target instanceof Element)
    )
      return;
    const details = event.target.closest<HTMLDetailsElement>(
      '[data-calendar-more-menu]'
    );
    if (!details) return;
    const summary = details.querySelector<HTMLElement>(':scope > summary');
    const items = Array.from(
      details.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]:not(:disabled)'
      )
    );
    if (!items.length) return;
    const isSummary = event.target.closest('summary') === summary;
    if (isSummary && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      if (summary?.getAttribute('aria-expanded') !== 'true') summary?.click();
      (event.key === 'ArrowDown' ? items.at(0) : items.at(-1))?.focus();
      return;
    }
    const index = items.indexOf(event.target as HTMLButtonElement);
    if (
      index < 0 ||
      !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
    )
      return;
    event.preventDefault();
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) %
            items.length;
    items.at(next)?.focus();
  };
  private readonly onFeedback = (event: Event) => {
    if (event.type === 'pointerover' && event.target instanceof Element) {
      const control = event.target.closest('button,summary,[role="button"]');
      const previous = (event as PointerEvent).relatedTarget;
      if (previous instanceof Node && control?.contains(previous)) return;
    }
    this.pulse(event.target);
  };
  update(root: HTMLElement): void {
    if (this.root === root) return;
    this.dispose();
    this.root = root;
    root.addEventListener('pointerover', this.onFeedback);
    root.addEventListener('focusin', this.onFeedback);
    root.addEventListener('click', this.onClick);
    root.addEventListener('keydown', this.onMenuKey);
    root.addEventListener('focusout', this.onFocusOut);
    this.releaseOutside = onClickOutside(
      root,
      () => this.closeMenus(),
      'click',
      true
    );
  }
  dispose(): void {
    this.root?.removeEventListener('pointerover', this.onFeedback);
    this.root?.removeEventListener('focusin', this.onFeedback);
    this.root?.removeEventListener('click', this.onClick);
    this.root?.removeEventListener('keydown', this.onMenuKey);
    this.root?.removeEventListener('focusout', this.onFocusOut);
    this.releaseOutside?.();
    this.releaseOutside = undefined;
    this.root = undefined;
    this.handles.forEach(handle => handle.cancel());
    this.handles.clear();
  }
}
class CalendarFeedbackDirective extends AsyncDirective {
  private readonly motion = new CalendarFeedbackMotion();
  private element: HTMLElement | undefined;
  constructor(part: PartInfo) {
    super(part);
    if (part.type !== PartType.ELEMENT)
      throw new Error('Calendar feedback requires an element');
  }
  render() {
    return noChange;
  }
  override update(part: ElementPart) {
    this.element = part.element as HTMLElement;
    this.motion.update(this.element);
    return noChange;
  }
  protected override disconnected() {
    this.motion.dispose();
  }
  protected override reconnected() {
    if (this.element) this.motion.update(this.element);
  }
}
export const calendarFeedbackMotion = directive(CalendarFeedbackDirective);

class CalendarRegionDirective extends AsyncDirective {
  private element: HTMLElement | undefined;
  private open: boolean | undefined;
  private requested = false;
  private revision = 0;
  constructor(part: PartInfo) {
    super(part);
    if (part.type !== PartType.ELEMENT)
      throw new Error('Calendar region requires an element');
  }
  render(_open: boolean) {
    return noChange;
  }
  override update(part: ElementPart, [open]: [boolean]) {
    this.element = part.element as HTMLElement;
    this.requested = open;
    if (this.open === open) return noChange;
    const initial = this.open === undefined;
    this.open = open;
    const revision = ++this.revision;
    queueMicrotask(() => {
      if (!this.isConnected || revision !== this.revision || !this.element)
        return;
      if (initial)
        streamLainAnimationController.settleRegion(this.element, open);
      else streamLainAnimationController.reveal(this.element, open);
    });
    return noChange;
  }
  protected override disconnected() {
    ++this.revision;
    if (this.element)
      streamLainAnimationController.settleRegion(this.element, this.requested);
    this.open = undefined;
  }
  protected override reconnected() {
    if (this.element)
      streamLainAnimationController.settleRegion(this.element, this.requested);
    this.open = this.requested;
  }
}
export const calendarRegionMotion = directive(CalendarRegionDirective);

/** Existing source menus retain their semantics; this source scope owns their enter handle. */
export function animateCalendarSourcePopup(popup: MenuHandler): MenuHandler {
  const element = popup.menu.menuElement;
  element.style.animation = 'none';
  streamLainAnimationController.play(
    element,
    STREAMLAIN_PANEL_LAYER_ENTER,
    'enter'
  );
  const onClose = popup.menu.options.onClose;
  popup.menu.options.onClose = () => {
    streamLainAnimationController.cancel(element);
    onClose?.();
  };
  return popup;
}

/** Local loading indicators use the same enter lifecycle as other calendar feedback. */
class CalendarActivityDirective extends AsyncDirective {
  private element: HTMLElement | undefined;
  constructor(part: PartInfo) {
    super(part);
    if (part.type !== PartType.ELEMENT)
      throw new Error('Calendar activity requires an element');
  }
  render() {
    return noChange;
  }
  override update(part: ElementPart) {
    const element = part.element as HTMLElement;
    if (this.element !== element) {
      this.cancel();
      this.element = element;
      this.enter();
    }
    return noChange;
  }
  private enter() {
    if (this.element)
      streamLainAnimationController.play(
        this.element,
        [{ opacity: 0 }, { opacity: 1 }],
        'enter'
      );
  }
  private cancel() {
    if (this.element) streamLainAnimationController.cancel(this.element);
  }
  protected override disconnected() {
    this.cancel();
  }
  protected override reconnected() {
    this.enter();
  }
}
export const calendarActivityMotion = directive(CalendarActivityDirective);
